import { useEffect, useState } from "react";
import { Bell, Trash2, Power } from "lucide-react";
import { toast } from "sonner";
import { api, useApi } from "../lib/api";
import { SectionHead, Skel } from "../components/common";
import usePageMeta from "../lib/usePageMeta";

const CONDITIONS = [
  ["price_above", "Price above target"], ["price_below", "Price below target"],
  ["volume_spike", "Volume increase"], ["holders_change", "Holder count change"],
  ["large_transfer", "Large transfer detected"], ["new_holder_concentration", "Holder concentration change"],
];

export default function Alerts() {
  usePageMeta("Alerts", "Create price and activity alerts for Robinhood Chain tokens.");
  const { data: alerts, mutate, loading } = useApi("/alerts");
  const { data: tokens } = useApi("/tokens?sort=volume");
  const [form, setForm] = useState({ target_id: "", condition: "price_above", value: "" });

  const targets = (tokens?.items || []).map((t) => ({ id: t.address, label: `${t.symbol} — ${t.name}` }));

  useEffect(() => {
    if (!form.target_id && targets.length) setForm((f) => ({ ...f, target_id: targets[0].id }));
  }, [tokens]); // eslint-disable-line

  const create = async (e) => {
    e.preventDefault();
    const t = targets.find((x) => x.id === form.target_id);
    await api.post("/alerts", {
      target_type: "token", target_id: form.target_id, target_label: t?.label?.split(" — ")[0] || form.target_id,
      condition: form.condition, value: form.value ? parseFloat(form.value) : null,
    });
    toast.success("Alert created");
    setForm({ ...form, value: "" });
    mutate();
  };

  const needsValue = ["price_above", "price_below", "volume_spike"].includes(form.condition);

  return (
    <div className="space-y-4">
      <SectionHead title="Alerts" />
      <p className="text-xs text-[#6B7280] -mt-2">Alerts are stored and shown in-app. Telegram, email, push and browser notifications are prepared for a later release.</p>

      <form onSubmit={create} className="t-card p-4 grid sm:grid-cols-4 gap-2 items-end" data-testid="alert-form">
        <label className="flex flex-col gap-1">
          <span className="text-[10px] uppercase text-[#6B7280]">Asset</span>
          <select data-testid="alert-target-select" value={form.target_id} onChange={(e) => setForm({ ...form, target_id: e.target.value })}
            className="bg-[#0A0A0A] border border-[#262626] rounded-sm px-2 py-2 text-[13px] outline-none focus:border-[#059669]">
            {targets.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
          </select>
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-[10px] uppercase text-[#6B7280]">Condition</span>
          <select data-testid="alert-condition-select" value={form.condition} onChange={(e) => setForm({ ...form, condition: e.target.value })}
            className="bg-[#0A0A0A] border border-[#262626] rounded-sm px-2 py-2 text-[13px] outline-none focus:border-[#059669]">
            {CONDITIONS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-[10px] uppercase text-[#6B7280]">Target value {needsValue ? "" : "(optional)"}</span>
          <input data-testid="alert-value-input" type="number" step="any" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })}
            placeholder={needsValue ? "e.g. 1.25" : "—"} required={needsValue}
            className="bg-[#0A0A0A] border border-[#262626] rounded-sm px-2 py-2 num text-[13px] outline-none focus:border-[#059669]" />
        </label>
        <button data-testid="alert-create-button" type="submit" className="flex items-center justify-center gap-2 px-4 py-2 bg-[#059669] hover:bg-[#047857] text-white text-[13px] rounded-sm transition-colors">
          <Bell size={14} /> Create Alert
        </button>
      </form>

      <div className="t-card" data-testid="alerts-list">
        {loading ? <Skel rows={4} h={36} /> : !alerts?.length ? (
          <div className="p-8 text-center text-[#6B7280] text-sm" data-testid="alerts-empty">No alerts yet. Create your first alert above.</div>
        ) : alerts.map((a) => (
          <div key={a.id} className="flex items-center gap-3 px-3 py-2.5 border-b border-[#1a1a1a] last:border-0" data-testid={`alert-row-${a.id}`}>
            <span className={`w-2 h-2 rounded-full ${a.active ? "bg-[#10B981]" : "bg-[#3F3F46]"}`} />
            <span className="text-[13px] font-medium">{a.target_label}</span>
            <span className="text-xs text-[#9CA3AF]">{CONDITIONS.find(([k]) => k === a.condition)?.[1] || a.condition}</span>
            {a.value != null && <span className="num text-xs text-[#10B981]">{a.value}</span>}
            <span className="ml-auto text-[10px] text-[#6B7280]">{new Date(a.created_at).toLocaleString()}</span>
            <button data-testid={`alert-toggle-${a.id}`} onClick={async () => { await api.patch(`/alerts/${a.id}/toggle`); mutate(); }}
              className={`transition-colors ${a.active ? "text-[#10B981]" : "text-[#6B7280]"}`}><Power size={14} /></button>
            <button data-testid={`alert-delete-${a.id}`} onClick={async () => { await api.delete(`/alerts/${a.id}`); toast("Alert deleted"); mutate(); }}
              className="text-[#6B7280] hover:text-[#EF4444] transition-colors"><Trash2 size={14} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}
